import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { toast } from "@/components/portal/Toast"

/**
 * Globaler Event-Watcher: pollt die KI-Telefon-Daten und macht NEUE Anrufe
 * unuebersehbar — Chime (Ton) + Tab-Titel-/Favicon-Badge + Toast.
 *
 * - Funktioniert solange die App im Browser offen ist (kein Push-Abo noetig).
 * - Baseline beim ersten Laden: bestehende Anrufe loesen KEINEN Alarm aus.
 * - Badge bleibt sichtbar bis der/die Nutzer:in die KI-Telefon-Seite oeffnet.
 */

type Call = {
  started_at?: string
  caller_name?: string
  caller_number?: string
  ai_headline?: string
  flag_severity?: string | null
}
type TelefonData = { stats?: { total?: number }; calls?: Call[] }

let _origTitle = ""
let _origFavicon: string | null = null
let _audioUnlocked = false

function playChime() {
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext
    if (!Ctx) return
    const ctx = new Ctx()
    const now = ctx.currentTime
    // Zwei-Ton "Ding-Dong"
    ;[[880, 0], [1175, 0.16]].forEach(([freq, dt]) => {
      const o = ctx.createOscillator()
      const g = ctx.createGain()
      o.connect(g); g.connect(ctx.destination)
      o.type = "sine"
      o.frequency.value = freq
      const t = now + dt
      g.gain.setValueAtTime(0.0001, t)
      g.gain.exponentialRampToValueAtTime(0.3, t + 0.02)
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.45)
      o.start(t); o.stop(t + 0.5)
    })
  } catch { /* Audio blockiert (kein Gesture) -> Badge/Titel reichen */ }
}

function setBadge(count: number) {
  if (!_origTitle) _origTitle = document.title || "werkXpartner"
  document.title = count > 0 ? `(${count > 9 ? "9+" : count}) ${_origTitle}` : _origTitle

  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null
  if (_origFavicon === null) _origFavicon = link ? link.href : ""
  if (count <= 0) {
    if (link && _origFavicon) link.href = _origFavicon
    return
  }
  try {
    const c = document.createElement("canvas")
    c.width = 64; c.height = 64
    const g = c.getContext("2d")
    if (!g) return
    g.fillStyle = "#dc2626"
    g.beginPath(); g.arc(32, 32, 30, 0, 2 * Math.PI); g.fill()
    g.fillStyle = "#fff"
    g.font = "bold 40px sans-serif"
    g.textAlign = "center"; g.textBaseline = "middle"
    g.fillText(count > 9 ? "9+" : String(count), 32, 35)
    if (!link) {
      link = document.createElement("link")
      link.rel = "icon"
      document.head.appendChild(link)
    }
    link.href = c.toDataURL("image/png")
  } catch { /* canvas/favicon optional */ }
}

export function useEventAlerts() {
  const loc = useLocation()
  const prevTotal = useRef<number | null>(null)
  const unseen = useRef(0)

  // Audio erst nach erster User-Interaktion entsperrt (Browser-Policy)
  useEffect(() => {
    if (_audioUnlocked) return
    const unlock = () => {
      _audioUnlocked = true
      window.removeEventListener("pointerdown", unlock)
      window.removeEventListener("keydown", unlock)
    }
    window.addEventListener("pointerdown", unlock)
    window.addEventListener("keydown", unlock)
    return () => {
      window.removeEventListener("pointerdown", unlock)
      window.removeEventListener("keydown", unlock)
    }
  }, [])

  const { data } = useQuery<TelefonData>({
    queryKey: ["ki-telefon-data"],
    queryFn: async () => {
      const res = await fetch("/admin/ki-telefon/api/data", { credentials: "include" })
      if (!res.ok) throw new Error("ki-telefon data")
      return res.json()
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    const total = data?.stats?.total
    if (typeof total !== "number") return
    if (prevTotal.current === null) {
      prevTotal.current = total // Baseline — bestehende Anrufe nicht alarmieren
      return
    }
    if (total > prevTotal.current) {
      const n = total - prevTotal.current
      prevTotal.current = total
      // Pro-Geraet ein/aus (Sidebar-Glocke). Aus -> kein Alarm, Baseline aktualisiert.
      if (localStorage.getItem("notifyAlerts") === "off") return
      // Auf der KI-Telefon-Seite kein Badge aufbauen (man sieht es ja live)
      const onPage = loc.pathname.startsWith("/ki-telefon")
      const newest = (data?.calls || [])[0]
      const who = newest?.caller_name || newest?.caller_number || "Unbekannt"
      const flagged = !!(newest?.flag_severity || "").trim()
      playChime()
      toast(
        flagged ? `⚠️ Neuer Anruf (Eskalation): ${who}` : `📞 Neuer Anruf: ${who}`,
        flagged ? "warn" : "success",
      )
      if (!onPage) {
        unseen.current += n
        setBadge(unseen.current)
      }
    }
  }, [data, loc.pathname])

  // Badge zuruecksetzen, sobald die KI-Telefon-Seite offen ist
  useEffect(() => {
    if (loc.pathname.startsWith("/ki-telefon")) {
      unseen.current = 0
      setBadge(0)
    }
  }, [loc.pathname])
}
